'use client'

import type * as React from 'react'

import { cn } from '@/shared/helpers/styles.helper'
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbText
} from '@/shared/ui/breadcrumb'

import StepCurrentIcon from './assets/step-current.svg'
import StepDoneIcon from './assets/step-done.svg'
import StepUpcomingIcon from './assets/step-upcoming.svg'

export type StepState = 'done' | 'current' | 'upcoming'

export interface StepsBreadcrumbItem {
  key: string
  label: React.ReactNode
  state: StepState
  // Opens the step; a step without it cannot be opened
  onOpen?: () => void
}

type Props = Omit<React.ComponentProps<'nav'>, 'children'> & {
  items: StepsBreadcrumbItem[]
}

const labelClassByState: Record<StepState, string> = {
  done: 'font-medium text-[color:var(--gray-800)]',
  current: 'font-semibold text-primary',
  upcoming: 'font-normal text-[color:var(--gray-500)]'
}

// Icon of a step state: check once done, ringed dot when current, grey dot when upcoming
const StepIcon = ({ state }: { state: StepState }) => {
  if (state === 'done')
    return (
      <span className='relative size-[18px] shrink-0'>
        <StepDoneIcon aria-hidden className='absolute -inset-[2px]' />
      </span>
    )
  if (state === 'current')
    return <StepCurrentIcon aria-hidden className='shrink-0' />
  return <StepUpcomingIcon aria-hidden className='shrink-0' />
}

// Vertical breadcrumb of the steps of a funnel: each step with its state, the steps that can be opened are buttons
export function StepsBreadcrumb({ items, className, ...props }: Props) {
  return (
    <Breadcrumb className={className} {...props}>
      <BreadcrumbList className='flex-col items-start gap-2 text-[15px] leading-5'>
        {items.map(({ key, label, state, onOpen }) => {
          const content = (
            <>
              <StepIcon state={state} />
              <span className='text-left'>{label}</span>
            </>
          )
          const itemClassName = cn(
            'flex items-center gap-4 py-0.5',
            labelClassByState[state]
          )
          return (
            <BreadcrumbItem key={key}>
              {onOpen ? (
                <BreadcrumbLink
                  render={<button type='button' onClick={onOpen} />}
                  aria-current={state === 'current' ? 'step' : undefined}
                  className={cn(
                    itemClassName,
                    'cursor-pointer hover:underline'
                  )}
                >
                  {content}
                </BreadcrumbLink>
              ) : (
                <BreadcrumbText className={itemClassName}>
                  {content}
                </BreadcrumbText>
              )}
            </BreadcrumbItem>
          )
        })}
      </BreadcrumbList>
    </Breadcrumb>
  )
}
